import React, { useRef, useState } from 'react'
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react'
import SwiperCore from 'swiper'

import { Icon } from '@/components/Icon'
import { Section } from '@components/Sections/Section'
import { ResponsiveImage } from '@components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import type { CMSSectionNutritionHero } from '@/types/sanity'

import config from 'tailwind.config'

// Import Swiper styles
import 'swiper/scss'
import 'swiper/scss/pagination'
import 'swiper/css/free-mode'
import 'swiper/scss/navigation'
import 'swiper/css/thumbs'
import {
  FreeMode,
  Navigation,
  Pagination,
  Thumbs,
  Mousewheel,
} from 'swiper/modules'

import './SectionNutritionHero.scss'
import useMediaQuery from '@/hooks/useMediaQuery'

type NutritionHeroSectionProp = {
  content: CMSSectionNutritionHero
}

export const SectionNutritionHero: React.FC<NutritionHeroSectionProp> = ({
  content,
}) => {
  const [selectedCarouselItem, setSelectedCarouselItem] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null)
  const [isCurrentSlideVideo, setIsCurrentSlideVideo] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const swiperRef = useRef<SwiperCore | null>(null)

  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.lg})`,
    true,
  )

  const handleSlideChange = (swiper: SwiperClass) => {
    swiperRef.current = swiper
    setSelectedCarouselItem(swiper.realIndex)

    const activeSlide = swiper.slides[swiper.activeIndex]
    const videoEl = activeSlide?.querySelector('video')
    setIsCurrentSlideVideo(!!videoEl)
  }

  const showPagination = !(isCurrentSlideVideo && isVideoPlaying)

  const {
    _id,
    title,
    theme,
    pricing,
    tags,
    description,
    imageryCarousel,
    cta,
  } = content

  return (
    <Section hasWrapper={false} id={_id} size="md" theme={theme}>
      <div className="nutrition-hero-section">
        {isDesktopScreen && (
          <div className="nutrition-hero__carousel">
            <div className="nutrition-hero__carousel-list">
              <Swiper
                spaceBetween={10}
                slidesPerView={'auto'}
                direction={'vertical'}
                touchEventsTarget="container"
                freeMode={true}
                mousewheel={true}
                modules={[FreeMode, Thumbs, Mousewheel]}
                onSwiper={setThumbsSwiper}
              >
                {imageryCarousel.map((carouselItem, index) => (
                  <SwiperSlide
                    key={index}
                    className={`carousel-item ${
                      index === selectedCarouselItem && 'active-carousel-item'
                    }`}
                  >
                    {carouselItem._type === 'component.responsiveImage' && (
                      <ResponsiveImage
                        document={carouselItem}
                        overrides={{
                          customSizes: [640, 768, 1024, 1200],
                        }}
                      />
                    )}
                    {carouselItem._type === 'component.video' && (
                      <ResponsiveImage
                        document={carouselItem.backupImage}
                        overrides={{
                          customSizes: [640, 768, 1024, 1200],
                        }}
                      />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <div className="selected-carousel-slide">
              <Swiper
                onSlideChange={handleSlideChange}
                thumbs={{ swiper: thumbsSwiper }}
                modules={[Pagination, Navigation, FreeMode, Thumbs]}
                slidesPerView={1}
                pagination={showPagination ? { clickable: true } : false}
                scrollbar={{ draggable: true }}
                navigation={true}
                loop={true}
                initialSlide={selectedCarouselItem}
              >
                {imageryCarousel.map((carouselItem, index) => {
                  return (
                    <SwiperSlide key={index} className="carousel-swiper-slide">
                      <div
                        className={`${
                          index === selectedCarouselItem &&
                          'active-carousel-item'
                        }`}
                      >
                        {carouselItem._type === 'component.responsiveImage' && (
                          <ResponsiveImage
                            document={carouselItem}
                            overrides={{
                              customSizes: [640, 768, 1024, 1200],
                            }}
                          />
                        )}
                        {carouselItem._type === 'component.video' && (
                          <Video
                            classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                            videoUrl={carouselItem.video.secureUrl}
                            videoType={carouselItem.videoType}
                            dialogueThumbnail={carouselItem.dialogue?.thumbnail}
                            includeControls={
                              carouselItem.dialogue?.includeControls
                            }
                            loopVideo={carouselItem.background?.loopVideo}
                            isFirstSection={false}
                            backupImage={carouselItem.backupImage}
                            onPlayingChange={setIsVideoPlaying}
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          </div>
        )}
        <div className="nutrition-hero__buying-panel">
          <div className="buying-panel__top-block">
            <h1
              className="nutrition-hero__title"
              dangerouslySetInnerHTML={{ __html: title }}
            ></h1>
            <div className="nutrition-hero__tags">
              {tags?.map((tag, i) => (
                <div
                  className={`nutrition-hero__tag type-label ${
                    tag.theme ? 'theme-' + tag.theme : ''
                  }`}
                  key={`tag-${i}`}
                >
                  <span>{tag.label}</span>
                </div>
              ))}
            </div>

            {!isDesktopScreen && (
              <div className="swiper-container">
                <Swiper
                  spaceBetween={12}
                  slidesPerView="auto"
                  initialSlide={selectedCarouselItem}
                  pagination={{ clickable: true }}
                  scrollbar={{ draggable: true }}
                  loop={true}
                  touchEventsTarget="container"
                >
                  {imageryCarousel.map((carouselItem, index) => {
                    return (
                      <SwiperSlide key={index} className="swiper-slide">
                        {carouselItem._type === 'component.responsiveImage' ? (
                          <ResponsiveImage
                            document={carouselItem}
                            overrides={{
                              customSizes: [640, 768, 1024, 1200],
                            }}
                          />
                        ) : (
                          <Video
                            classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                            videoUrl={carouselItem.video.secureUrl}
                            videoType={carouselItem.videoType}
                            dialogueThumbnail={carouselItem.dialogue?.thumbnail}
                            includeControls={
                              carouselItem.dialogue?.includeControls
                            }
                            loopVideo={carouselItem.background?.loopVideo}
                            isFirstSection={false}
                            backupImage={carouselItem.backupImage}
                          />
                        )}
                      </SwiperSlide>
                    )
                  })}
                </Swiper>
              </div>
            )}

            {pricing && (
              <div className="nutrition-hero__pricing pricing-mobile">
                <p className="content-title">{pricing}</p>
              </div>
            )}
            {description && (
              <div className="nutrition-hero__description">
                <p className="type-label content-title">Description:</p>
                <p>{description}</p>
              </div>
            )}
          </div>
          <div className="buying-panel__footer-block">
            {pricing && (
              <div className="nutrition-hero__pricing pricing-desktop">
                <p className="type-label content-title">Pricing:</p>
                <p>{pricing}</p>
              </div>
            )}

            <a
              href={
                cta.destination == 'url'
                  ? cta.destinationUrl
                  : `#${cta.anchor?._id}`
              }
              target={
                cta.destination == 'url' && cta.isExternal ? '_blank' : ''
              }
              className={`buying-panel__action-button buying-panel__action-button--${cta.variant}`}
            >
              <div>{cta.title}</div>
              <div className="buying-panel__action-button-icon">
                <Icon name="primary-arrow-right" viewBox="0 0 21 11" />
              </div>
            </a>
          </div>
        </div>
      </div>
    </Section>
  )
}
